/**
 * PerformanceScoreCard Component
 * Circular gauge showing overall performance score with color coding
 */

import React from 'react';
import { clsx } from 'clsx';
import { Card } from '../common';
import { getScoreColor, getScoreCategory } from '../../types';

export interface PerformanceScoreCardProps {
  score: number;
  title?: string;
  subtitle?: string;
  size?: 'sm' | 'md' | 'lg';
  showCategory?: boolean;
  className?: string;
  isLoading?: boolean;
}

const CATEGORY_LABELS = {
  excellent: 'Excellent',
  good: 'Good',
  needsImprovement: 'Needs Improvement',
  poor: 'Poor',
};

export const PerformanceScoreCard: React.FC<PerformanceScoreCardProps> = ({
  score,
  title = 'Overall Performance',
  subtitle,
  size = 'md',
  showCategory = true,
  className,
  isLoading = false,
}) => {
  const category = getScoreCategory(score);
  const color = getScoreColor(score);

  // Calculate circumference for the circular progress
  const sizes = {
    sm: { radius: 40, stroke: 6, fontSize: 'text-2xl' },
    md: { radius: 60, stroke: 8, fontSize: 'text-4xl' },
    lg: { radius: 80, stroke: 10, fontSize: 'text-5xl' },
  };

  const { radius, stroke, fontSize } = sizes[size];
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (score / 100) * circumference;
  const svgSize = (radius + stroke) * 2;
  const center = radius + stroke;

  if (isLoading) {
    return (
      <Card className={clsx('p-6', className)}>
        <div className="animate-pulse flex flex-col items-center">
          <div
            className="rounded-full bg-stone-200"
            style={{ width: svgSize, height: svgSize }}
          />
          <div className="h-4 bg-stone-200 rounded w-32 mt-4" />
          <div className="h-3 bg-stone-200 rounded w-24 mt-2" />
        </div>
      </Card>
    );
  }

  return (
    <Card className={clsx('p-6', className)}>
      <div className="flex flex-col items-center">
        {/* Title */}
        <h3 className="text-lg font-semibold text-stone-800 mb-4">{title}</h3>

        {/* Circular Progress */}
        <div className="relative" style={{ width: svgSize, height: svgSize }}>
          <svg
            width={svgSize}
            height={svgSize}
            className="transform -rotate-90"
          >
            {/* Background circle */}
            <circle
              cx={center}
              cy={center}
              r={radius}
              fill="none"
              stroke="#E5E7EB"
              strokeWidth={stroke}
            />
            {/* Progress circle */}
            <circle
              cx={center}
              cy={center}
              r={radius}
              fill="none"
              stroke={color}
              strokeWidth={stroke}
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              className="transition-all duration-700 ease-out"
            />
          </svg>

          {/* Score in center */}
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className={clsx(fontSize, 'font-bold')} style={{ color }}>
              {Math.round(score)}
            </span>
            <span className="text-sm text-stone-500">/ 100</span>
          </div>
        </div>

        {/* Category label */}
        {showCategory && (
          <div className="mt-4 text-center">
            <span
              className={clsx(
                'inline-block px-3 py-1 rounded-full text-sm font-medium',
                category === 'excellent' && 'bg-green-100 text-green-700',
                category === 'good' && 'bg-blue-100 text-blue-700',
                category === 'needsImprovement' && 'bg-amber-100 text-amber-700',
                category === 'poor' && 'bg-red-100 text-red-700'
              )}
            >
              {CATEGORY_LABELS[category]}
            </span>
          </div>
        )}

        {/* Subtitle */}
        {subtitle && (
          <p className="mt-2 text-sm text-stone-500 text-center">{subtitle}</p>
        )}
      </div>
    </Card>
  );
};

export default PerformanceScoreCard;
